import restClient from "./api";
import NProgress from "nprogress";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { mustGetEnv } from "@/utils/env";
import { convertFileToDraco, isDraco, convertDracoToSTL } from "@/utils/dracoUtils";

let baseURLFileService = mustGetEnv("VUE_APP_FILESERVICE_BASEURL");

class FileService {
  /*
    file - An actual object of type File
    json  -  {
              "user_ids":["1"], (optional)
              "roles":["1"]     (optional)
            }
  */
  async uploadFile(file, json, startProgress = 0, maxContribution = 1, convertToDraco = false) {
    const ext = file.name.toLowerCase().split(".").pop();
    const fileToUpload = convertToDraco && ["stl", "ply", "obj"].includes(ext)
      ? await convertFileToDraco(file)
      : file;

    const formData = new FormData();
    formData.append("upload_files", fileToUpload);
    formData.append("data", JSON.stringify(json));

    return restClient.post(
      baseURLFileService + "/upload-file",
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
      {
        onUploadProgress: function (
          startProgress,
          maxContribution,
          progressEvent
        ) {
          var percentage =
            startProgress +
            (progressEvent.loaded / progressEvent.total) * maxContribution;
          console.log(percentage);
          if (percentage > 0.95) {
            NProgress.done();
          } else {
            NProgress.set(percentage);
          }
        }.bind(this, startProgress, maxContribution),
      }
    );
  }

  listFileById(fileId) {
    return restClient.get(
      baseURLFileService + "/list-file-by-id?id=" + fileId,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  }

  async getFileById(fileId) {
    const response = await restClient.get(
      baseURLFileService + "/files/" + fileId,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "arraybuffer",
      },
      {
        onDownloadProgress: function (progressEvent) {
          var percentage = progressEvent.loaded / progressEvent.total;
          if (percentage > 0.9) {
            NProgress.done();
          } else {
            NProgress.set(percentage);
          }
        }.bind(this),
      }
    );
    response.data = isDraco(response.data)
      ? await convertDracoToSTL(response.data)
      : new Blob([response.data]);
    return response;
  }

  async getResultFileById(fileId) {
    const response = await restClient.get(
      baseURLFileService + "/files/" + fileId,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "arraybuffer",
      },
      {
        onDownloadProgress: function (progressEvent) {
          var percentage = progressEvent.loaded / progressEvent.total;
          if (percentage > 0.9) {
            NProgress.done();
          } else {
            NProgress.set(percentage);
          }
        }.bind(this),
      }
    );
    if (isDraco(response.data)) {
      const stlBlob = await convertDracoToSTL(response.data);
      response.data = await stlBlob.arrayBuffer();
    }
    return response;
  }

  async getResultById(fileId) {
    const response = await restClient.get(
      baseURLFileService + "/files/" + fileId,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        responseType: "arraybuffer",
      },
      {
        onDownloadProgress: function (progressEvent) {
          var percentage = progressEvent.loaded / progressEvent.total;
          if (percentage > 0.9) {
            NProgress.done();
          } else {
            NProgress.set(percentage);
          }
        }.bind(this),
      }
    );
    response.data = isDraco(response.data)
      ? await convertDracoToSTL(response.data)
      : new Blob([response.data]);
    return response;
  }

  deleteFileById(fileId) {
    return restClient.post(
      baseURLFileService + "/delete-file",
      { id: fileId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  }

  /*
    {
      "id":"124",
      "directory_id":"102",
      "add_user_ids":["12"],
      "add_roles":["lawyer"],
      "remove_user_ids":["888"],
      "remove_roles":["customer"]
    }
  */
  updateFile(json) {
    return restClient.post(baseURLFileService + "/update-file", json, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  forceFileDownload(response, title) {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", title);
    document.body.appendChild(link);
    link.click();
  }

  downloadWithAxios(fileId, store) {
    store.commit("showLoader", true);
    this.listFileById(fileId).then(
      (response) => {
        if (response.data != null) {
          this.getFileById(fileId)
            .then((response2) => {
              let fileName = response.data.name || response.data.originalFileName;
              if (/\.drc$/i.test(fileName)) {
                fileName = fileName.replace(/\.drc$/i, ".stl");
              }
              this.forceFileDownload(response2, fileName);
            })
            .catch(() => {})
            .finally(() => {
              store.commit("showLoader", false);
            });
        } else {
          store.commit("showLoader", false);
        }
      },
      () => {
        store.commit("showLoader", false);
      }
    );
  }

  downloadRegisteredFiles(array, store) {
    for (var i = 0; i < array.length; i++) {
      if (array[i] == null || array[i] == undefined) {
        continue;
      }

      this.downloadRegisteredWithAxios(array[i], store);
    }
  }

  downloadRegisteredWithAxios(fileId, store) {
    store.commit("showLoader", true);
    this.listFileById(fileId).then(
      (response) => {
        if (response.data != null) {
          this.getFileById(fileId)
            .then((response2) => {
              let fileName = response.data.name || response.data.originalFileName;
              if (/\.(ply|obj|drc)$/i.test(fileName)) {
                fileName = fileName.replace(/\.(ply|obj|drc)$/i, ".stl");
              }
              this.forceFileDownload(response2, fileName);
            })
            .catch(() => {})
            .finally(() => {
              store.commit("showLoader", false);
            });
        } else {
          store.commit("showLoader", false);
        }
      },
      () => {
        store.commit("showLoader", false);
      }
    );
  }

  downloadFiles(array, store) {
    for (var i = 0; i < array.length; i++) {
      if (array[i] == null || array[i] == undefined) {
        continue;
      }

      this.downloadWithAxios(array[i], store);
    }
  }

  async downloadZipFiles(options, store) {
    const {
      files,
      useVersionNames = false,
      zipName = "download.zip",
    } = options;

    store.commit("showLoader", true);
    const zip = new JSZip();

    for (const file of files) {
      const fileId = useVersionNames ? file.id : file;
      if (!fileId) continue;

      try {
        const responseMeta = await this.listFileById(fileId);
        if (!responseMeta?.data) continue;

        let fileNameBase =
          responseMeta.data.name ||
          responseMeta.data.originalFileName ||
          `file_${fileId}`;

        if (/\.drc$/i.test(fileNameBase)) {
          fileNameBase = fileNameBase.replace(/\.drc$/i, ".stl");
        }

        let fileName = fileNameBase;

        if (useVersionNames && file.versionSuffix) {
          const dotIndex = fileNameBase.lastIndexOf(".");
          const versionSuffix = file.versionSuffix;

          if (dotIndex !== -1) {
            const name = fileNameBase.substring(0, dotIndex);
            const ext = fileNameBase.substring(dotIndex);
            fileName = `${name}_${versionSuffix}${ext}`;
          } else {
            fileName = `${fileNameBase}_${versionSuffix}`;
          }
        }

        const fileResponse = await this.getResultFileById(fileId);
        zip.file(fileName, new Blob([fileResponse.data]));
      } catch (error) {
        console.error(`Failed to process file ID ${fileId}:`, error);
      }
    }

    try {
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, zipName);
    } catch (zipError) {
      console.error("ZIP creation error:", zipError);
    } finally {
      store.commit("showLoader", false);
    }
  }
}

export default new FileService();
